import React, { createContext, useContext, useCallback, useRef, useEffect, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client';
import { useNotifications } from '../notifications';
import { queriesV2 } from '../api';
import { downloadPdfHelper } from '../components/document-table/helpers/downloadPdfHelper';

type ExportType = 'document' | 'template';

interface PdfExportRequest {
  id: string;
  type: ExportType;
  title: string;
  teamId?: string;
}

interface PendingExport {
  interval: ReturnType<typeof setInterval>;
  attempts: number;
}

interface PdfExportContextType {
  startPdfPolling: (request: PdfExportRequest) => void;
}

const PdfExportContext = createContext<PdfExportContextType | undefined>(undefined);

const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 20;

export const PdfExportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const apolloClient = useApolloClient();
  const [, { success: notifySuccess, info: notifyInfo, error: notifyError }] = useNotifications();
  const pendingExports = useRef<Map<string, PendingExport>>(new Map());

  // Cleanup all intervals on unmount (app close only)
  useEffect(() => {
    return () => {
      pendingExports.current.forEach((pending) => clearInterval(pending.interval));
      pendingExports.current.clear();
    };
  }, []);

  const clearExport = useCallback((key: string) => {
    const pending = pendingExports.current.get(key);
    if (pending) {
      clearInterval(pending.interval);
      pendingExports.current.delete(key);
    }
  }, []);

  const startPdfPolling = useCallback((request: PdfExportRequest) => {
    const key = `${request.type}-${request.id}`;

    // If already polling for this export, cancel the previous one
    clearExport(key);

    notifyInfo({ message: 'Preparing PDF. Download will begin shortly.' });

    let attempts = 0;

    const interval = setInterval(async () => {
      attempts++;

      try {
        if (request.type === 'document') {
          const { data } = await apolloClient.query({
            query: queriesV2.getGeneratedDocumentPdf,
            variables: { documentId: request.id },
            fetchPolicy: 'network-only',
          });
          const pdfUrl = data?.getDocumentById?.generatedDocumentPdfUrl;
          const pdfTitle = data?.getDocumentById?.title || request.title;
          if (pdfUrl) {
            clearExport(key);
            await downloadPdfHelper(pdfUrl, pdfTitle);
            notifySuccess({ message: 'PDF downloaded successfully.' });
            return;
          }
        } else {
          const { data } = await apolloClient.query({
            query: queriesV2.getGeneratedTemplatePdf,
            variables: { documentTemplateId: request.id, teamId: request.teamId },
            fetchPolicy: 'network-only',
          });
          const pdfUrl = data?.getDocumentTemplateById?.generatedDocumentPdfUrl;
          const pdfTitle = data?.getDocumentTemplateById?.name || request.title;
          if (pdfUrl) {
            clearExport(key);
            await downloadPdfHelper(pdfUrl, pdfTitle);
            notifySuccess({ message: 'PDF downloaded successfully.' });
            return;
          }
        }

        if (attempts >= MAX_POLL_ATTEMPTS) {
          clearExport(key);
          const msg = request.type === 'document'
            ? 'PDF is still generating. Check the Activity feed for the download link.'
            : 'PDF is still generating. Please try again shortly.';
          notifyInfo({ message: msg });
        }
      } catch (err) {
        console.error('PDF poll error:', err);
        if (attempts >= MAX_POLL_ATTEMPTS) {
          clearExport(key);
          notifyError({ message: 'Failed to check PDF status.', pinned: true });
        }
      }
    }, POLL_INTERVAL_MS);

    pendingExports.current.set(key, { interval, attempts });
  }, [apolloClient, clearExport, notifyInfo, notifySuccess, notifyError]);

  return (
    <PdfExportContext.Provider value={{ startPdfPolling }}>
      {children}
    </PdfExportContext.Provider>
  );
};

export const usePdfExport = () => {
  const context = useContext(PdfExportContext);
  if (!context) {
    throw new Error('usePdfExport must be used within a PdfExportProvider');
  }
  return context;
};
